/**
 * Raw onboarding draft → rulebook adapter. ONE copy, for every runtime.
 *
 * The wizard and the completion gate used to hold hand-mirrored copies of this
 * adapter, one in each app. Keeping two copies in step by hand is what shipped
 * the C-Corp reserved-shares rejection, so the adapter lives here now and both
 * runtimes import it.
 *
 * The input is the RAW store state the wizard persists into `steps.review` —
 * the only shape the Worker ever sees. Handing that straight to the rulebook
 * would reject drafts the wizard legitimately accepts, so five normalizations
 * are applied first, and nothing else:
 *
 *   1. a new company has no address of its own yet, so its business address is
 *      our registered agent's;
 *   2. an address with no country is a US address — company addresses and a
 *      shareholder's home address alike;
 *   3. a C-Corp shareholder's share count is derived from their ownership
 *      percentage when the draft carries no explicit allocation — the wizard
 *      collects percentages, not share counts, and stores `numberOfShares: 0`;
 *   4. total shares fall back to the 10M default;
 *   5. an unpunctuated 9-digit tax id is formatted for its declared type — and
 *      ONLY an unpunctuated one.
 *
 * (5) is deliberately stricter than Product's old `formatEIN`, which reformatted
 * ANY nine digits and so silently turned an SSN typed under `taxIdType: 'ein'`
 * into a structurally valid EIN; the wizard passed and the completion request
 * then 400'd. A value the customer already punctuated is judged exactly as
 * entered, so `123-45-6789` under `ein` still fails. The stricter judgement is
 * the shared one — see `parity-cases.ts`, asymmetry 1.
 *
 * Every violation message is rulebook copy plus a field path. Tax ids, emails,
 * and names are never echoed — a 400 body must stay free of customer PII.
 */

import {
  DEFAULT_TOTAL_SHARES,
  validateExistingCompany,
  validateNewCompany,
} from "./company";
import { normalizeTaxIdType } from "./tax-id";
import type {
  AddressInput,
  ExistingCompanyInput,
  NewCompanyInput,
  RuleViolation,
  ShareholderInput,
} from "./types";

type JsonObject = Record<string, unknown>;

/**
 * Provider address used as a new company's initial business address, and as the
 * registered-agent address written at promotion. Single source of truth for
 * both runtimes.
 */
export const REGISTERED_AGENT_PRIMARY_ADDRESS = {
  type: "primary",
  street: "604 Carson Dr",
  city: "Bear",
  state: "DE",
  zipCode: "19701",
  country: "US",
} as const satisfies AddressInput;

const SHAREHOLDER_FIELD_PATTERN = /^shareholders\[(\d+)\]\./;

function asObject(value: unknown): JsonObject | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonObject)
    : null;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Format an unpunctuated tax id for its declared type. Anything the customer
 * punctuated themselves is returned untouched so a mistyped id stays a
 * violation instead of being silently rewritten into a valid one.
 */
export function canonicalTaxId(
  raw: string | null | undefined,
  declaredType: string | null | undefined,
): string | null {
  const value = readString(raw);
  if (value === null) return null;
  const type = normalizeTaxIdType(declaredType ?? null);
  if (type === null || type === "ftin" || !/^\d{9}$/.test(value)) return value;
  return type === "ein"
    ? `${value.slice(0, 2)}-${value.slice(2)}`
    : `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`;
}

/** Same canonicalization, as the plain string the transformers/schemas want. */
export function canonicalTaxIdText(
  raw: string | null | undefined,
  declaredType: string | null | undefined,
): string {
  return canonicalTaxId(raw, declaredType) ?? "";
}

/** Whitespace — `\s` already covers the non-breaking space a copied PDF carries. */
const TAX_ID_SPACING = /\s+/g;

/**
 * Hyphen lookalikes: the Unicode dash block (hyphen through horizontal bar,
 * where the en dash, the em dash, and the non-breaking hyphen all live), the
 * minus sign, and the small/full-width hyphen forms.
 */
const TAX_ID_DASHES = /[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g;

/**
 * Tax-id text as it is TYPED OR PASTED, before the draft stores it.
 *
 * A customer copying an EIN off an IRS letter brings the letter's separators
 * with it — `12 3456789`, or `12–3456789` with an en dash. `canonicalTaxId`
 * reads both as "the customer punctuated this themselves" and stores them
 * verbatim, so the step then rejects a number they copied correctly.
 *
 * Repairing the separators HERE is safe where repairing them inside
 * `canonicalTaxId` would not be: this runs at the input, and its result is what
 * the store holds and therefore what the Worker reads at completion, so both
 * runtimes still judge the same string. Use it for a value being entered, never
 * for one being validated.
 *
 * The guard that matters survives: normalizing a dash never regroups digits, so
 * an SSN-shaped `123-45-6789` stays 3-2-4 and stays invalid under `ein`.
 * Dropping spacing can leave a bare nine digits (`12 3456789` → `123456789`),
 * which is then formatted for the DECLARED type exactly as `canonicalTaxId`
 * already formats any unpunctuated nine-digit id.
 */
export function canonicalTaxIdEntry(
  raw: string | null | undefined,
  declaredType: string | null | undefined,
): string {
  if (raw == null) return "";
  return canonicalTaxIdText(
    raw.replace(TAX_ID_SPACING, "").replace(TAX_ID_DASHES, "-"),
    declaredType,
  );
}

interface OwnershipContext {
  isCCorp: boolean;
  availableShares: number;
  totalPercentage: number;
  shareholderCount: number;
}

function ruleShareholder(
  raw: unknown,
  context: OwnershipContext,
): ShareholderInput {
  const shareholder = asObject(raw) ?? {};
  const details = asObject(shareholder.ownershipDetails) ?? {};
  const kind = readString(shareholder.ownershipType)?.toLowerCase();

  // An absent type is the wizard's own default (individuals file an SSN,
  // company owners an EIN); a garbage value is passed through so the rulebook
  // reports it rather than having it masked by a default.
  const declaredTaxIdType =
    readString(shareholder.taxIdType) ?? (kind === "company" ? "ein" : "ssn");

  const percentage = readNumber(details.percentage);
  const explicitShares = readNumber(details.numberOfShares);
  let numberOfShares =
    explicitShares !== null && explicitShares > 0 ? explicitShares : null;
  if (context.isCCorp && numberOfShares === null && percentage !== null) {
    const share =
      context.totalPercentage > 0
        ? percentage / context.totalPercentage
        : 1 / Math.max(1, context.shareholderCount);
    numberOfShares = Math.floor(share * context.availableShares);
  }

  return {
    ownershipType: readString(shareholder.ownershipType),
    firstName: readString(shareholder.firstName),
    lastName: readString(shareholder.lastName),
    companyName: readString(shareholder.companyName),
    authorizedPersonFirstName: readString(
      shareholder.authorizedPersonFirstName,
    ),
    authorizedPersonLastName: readString(shareholder.authorizedPersonLastName),
    email: readString(shareholder.email),
    phone: readString(shareholder.phone),
    address: ruleAddress(shareholder.address),
    taxId: canonicalTaxId(readString(shareholder.taxId), declaredTaxIdType),
    taxIdType: declaredTaxIdType,
    ownershipDetails: {
      percentage,
      numberOfShares,
      signingAuthority: details.signingAuthority === true,
      boardMember: details.boardMember === true,
      officer: details.officer === true,
      secretary: details.secretary === true,
    },
  };
}

function ruleAddress(raw: unknown): AddressInput {
  const address = asObject(raw) ?? {};
  return {
    type: readString(address.type),
    street: readString(address.street),
    apartment: readString(address.apartment),
    city: readString(address.city),
    state: readString(address.state),
    zipCode: readString(address.zipCode),
    country: readString(address.country) ?? "US",
  };
}

/**
 * `reservedPercent` is passed in rather than read from the draft: the two flows
 * store that field in different units, and only the callers know which one they
 * are holding (see `reservedEquityPercentOfShares`).
 */
function ruleShareholders(
  draft: JsonObject,
  isCCorp: boolean,
  reservedPercent: number,
): ShareholderInput[] {
  const raw = readArray(draft.shareholders);
  const totalShares = readNumber(draft.totalShares) || DEFAULT_TOTAL_SHARES;
  const context: OwnershipContext = {
    isCCorp,
    availableShares: isCCorp
      ? Math.max(0, Math.floor(totalShares * (1 - reservedPercent / 100)))
      : 0,
    totalPercentage: raw.reduce<number>(
      (sum, item) =>
        sum +
        (readNumber(asObject(asObject(item)?.ownershipDetails)?.percentage) ??
          0),
      0,
    ),
    shareholderCount: raw.length,
  };
  return raw.map((item) => ruleShareholder(item, context));
}

function isCCorpDraft(draft: JsonObject): boolean {
  return readString(draft.type)?.toLowerCase() === "ccorp";
}

/**
 * The new-company reserve, as a PERCENTAGE, under either name it may carry.
 *
 * The wizard's store calls it `reservedEquityPercent`, because `reservedEquity`
 * named a percentage here and a share count in the existing-company flow — one
 * name, two units, which is how a C-Corp reserving 1,000,000 of 10,000,000
 * shares was once read as reserving 1,000,000 percent. The PERSISTED draft
 * still uses the old name: it is what the Worker reads at promotion, and its
 * storage is not this adapter's to change.
 *
 * So both names are accepted and both produce the same `reservedEquity` field
 * on the emitted `NewCompanyInput` — the rulebook's payload is unchanged
 * whichever runtime handed the draft over. Pinned by the `reservedEquityPercent`
 * cases in `parity-cases.ts`, which every runtime's parity suite runs.
 */
function newCompanyReservedPercent(state: JsonObject): number {
  return (
    readNumber(state.reservedEquityPercent) ??
    readNumber(state.reservedEquity) ??
    0
  );
}

/** Raw `newCompany` store state → the shape `validateNewCompany` expects. */
export function newCompanyRuleInput(draft: unknown): NewCompanyInput {
  const state = asObject(draft) ?? {};
  const isCCorp = isCCorpDraft(state);
  const addresses = readArray(state.addresses);
  const reservedPercent = isCCorp ? newCompanyReservedPercent(state) : 0;
  return {
    name: readString(state.name),
    type: readString(state.type),
    formationState: readString(state.formationState),
    totalShares: readNumber(state.totalShares) || DEFAULT_TOTAL_SHARES,
    // Already a percentage in this flow.
    reservedEquity: reservedPercent,
    shareholders: ruleShareholders(state, isCCorp, reservedPercent),
    addresses:
      addresses.length > 0
        ? addresses.map(ruleAddress)
        : [REGISTERED_AGENT_PRIMARY_ADDRESS],
  };
}

/**
 * `reservedEquity` is not the same unit in the two flows, and the rulebook
 * speaks only one of them.
 *
 * The NEW-company wizard collects a PERCENTAGE. The EXISTING-company wizard
 * collects a SHARE COUNT: `existing-company-shareholders.tsx` renders
 * `totalShares - reservedShares` and its own step validation requires
 * `allocated + reserved === totalShares`. Handing that share count to a rule
 * that bounds a percentage at 0–99 rejected every existing C-Corp reserving any
 * shares at all, so the count is converted here instead.
 *
 * A reserve larger than the company's own share count still fails the 0–99
 * bound, which is the right answer rather than a rounding artefact.
 */
function reservedEquityPercentOfShares(
  reservedShares: number | null | undefined,
  totalShares: number,
): number {
  if (reservedShares == null || Number.isNaN(reservedShares)) return 0;
  if (totalShares <= 0) return reservedShares === 0 ? 0 : 100;
  return (reservedShares / totalShares) * 100;
}

/**
 * The existing-company reserve, as a SHARE COUNT, under either name it may
 * carry.
 *
 * The wizard's store calls it `reservedShares`, for the same reason the other
 * flow's half became `reservedEquityPercent`: one name meant two units. The
 * PERSISTED draft still uses the old name — it is what the Worker reads at
 * completion, and its storage is not this adapter's to change.
 *
 * So both names are accepted and both produce the same `reservedEquity`
 * percentage on the emitted `ExistingCompanyInput` — the rulebook's payload is
 * unchanged whichever runtime handed the draft over. Pinned by the
 * `reservedShares` cases in `parity-cases.ts`, which every runtime's parity
 * suite runs.
 */
function existingCompanyReservedShares(state: JsonObject): number | null {
  return readNumber(state.reservedShares) ?? readNumber(state.reservedEquity);
}

/** Raw `existingCompany` store state → the shape `validateExistingCompany` expects. */
export function existingCompanyRuleInput(draft: unknown): ExistingCompanyInput {
  const state = asObject(draft) ?? {};
  const isCCorp = isCCorpDraft(state);
  const totalShares = readNumber(state.totalShares) || DEFAULT_TOTAL_SHARES;
  // A SHARE COUNT in this flow — converted before it reaches the rulebook.
  const reservedPercent = isCCorp
    ? reservedEquityPercentOfShares(
        existingCompanyReservedShares(state),
        totalShares,
      )
    : 0;
  return {
    name: readString(state.name),
    type: readString(state.type),
    formationState: readString(state.formationState),
    formationDate: readString(state.formationDate),
    ein: canonicalTaxId(readString(state.ein), "ein"),
    totalShares,
    reservedEquity: reservedPercent,
    shareholders: ruleShareholders(state, isCCorp, reservedPercent),
    addresses: readArray(state.addresses).map(ruleAddress),
  };
}

/**
 * Every rule that must hold before a new-company draft can reach checkout.
 *
 * `validateNewCompany` already applies the C-Corp role requirements. Appending
 * them again here reported every role violation twice, and the duplicates ate
 * into the capped violation list the 400 carries — real failures disappeared
 * behind "(+N more)".
 */
export function findNewCompanyViolations(
  draft: unknown,
  now: Date,
): RuleViolation[] {
  return validateNewCompany(newCompanyRuleInput(draft), now);
}

/** Every rule that must hold before an existing-company draft can be promoted. */
export function findExistingCompanyViolations(
  draft: unknown,
  now: Date,
): RuleViolation[] {
  return validateExistingCompany(existingCompanyRuleInput(draft), now);
}

/**
 * Index of the shareholder a violation belongs to, or null when the violation
 * is about the collection as a whole. Lets the wizard prefix a per-shareholder
 * message with that shareholder's name while leaving collection-level copy
 * ("LLC ownership percentages must total 100%") unprefixed.
 */
export function violationShareholderIndex(field: string): number | null {
  const match = SHAREHOLDER_FIELD_PATTERN.exec(field);
  return match ? Number(match[1]) : null;
}
